import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { postFetcher } from "@/lib/swr";
import { toastError } from "@/lib/toast";
import type { Version } from "@/types/domain";

type BuildMode = "apk" | "all";

interface BuildOptions {
  supported: boolean;
  needFlavors: boolean;
  flavors: { id: number; name: string }[];
}

const MODE_LABELS: Record<BuildMode, string> = {
  apk: "Test build",
  all: "Release build",
};

/**
 * Jenkins build triggers on the version detail header: test build → APK,
 * release build → APK + AAB. The server proxies Jenkins and owns the
 * credentials; a success response only means the job is queued, so the
 * toast says "submitted", never "built". JPSingle picks flavors first.
 */
const BuildButtons = ({ version }: { version: Version }) => {
  const { toast } = useToast();
  const { data: options } = useSWR<BuildOptions>([
    `/versions/${version.id}/build/options`,
  ]);
  const [flavorOpen, setFlavorOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<BuildMode>("apk");
  const [selected, setSelected] = useState<number[]>([]);

  const { trigger, isMutating } = useSWRMutation(
    `/versions/${version.id}/build`,
    postFetcher,
    {
      onSuccess: () => {
        setFlavorOpen(false);
        setSelected([]);
        toast({ description: "Build submitted. Jenkins will queue it now." });
      },
      onError: (error) => toastError(toast, "Build request failed", error),
    }
  );

  if (!options?.supported) return null;

  const submit = (mode: BuildMode, flavorPids?: number[]) =>
    trigger({ mode, ...(flavorPids?.length ? { flavorPids } : {}) });

  const onBuildClick = (mode: BuildMode) => {
    if (options.needFlavors) {
      setPendingMode(mode);
      setFlavorOpen(true);
    } else {
      void submit(mode);
    }
  };

  const toggleFlavor = (id: number, checked: boolean) =>
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );

  return (
    <>
      <Button
        variant="outline"
        className="h-8 px-3"
        disabled={isMutating}
        title="Build APK"
        onClick={() => onBuildClick("apk")}
      >
        Test build
      </Button>
      <Button
        variant="outline"
        className="h-8 px-3"
        disabled={isMutating}
        title="Build APK + AAB"
        onClick={() => onBuildClick("all")}
      >
        Release build
      </Button>
      <Dialog open={flavorOpen} onOpenChange={setFlavorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{MODE_LABELS[pendingMode]} · flavors</DialogTitle>
            <DialogDescription>
              JPSingle builds one package per selected flavor. Pick at least
              one.
            </DialogDescription>
          </DialogHeader>
          <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto">
            {options.flavors.map((f) => (
              <div key={f.id} className="flex items-center gap-2">
                <Checkbox
                  id={`flavor-${f.id}`}
                  checked={selected.includes(f.id)}
                  onCheckedChange={(checked) =>
                    toggleFlavor(f.id, checked === true)
                  }
                />
                <Label htmlFor={`flavor-${f.id}`} className="font-normal">
                  {f.name}
                </Label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              disabled={isMutating || selected.length === 0}
              onClick={() => void submit(pendingMode, selected)}
            >
              {isMutating
                ? "Submitting…"
                : `Submit ${selected.length ? `(${selected.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BuildButtons;
